import * as THREE from "three/webgpu";
import {AbstractSketchEdgeObject} from './AbstractSketchEdgeObject';
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import {SketchPointObject} from './SketchPointObject';
import {SketchPointKind} from './ISketchObject';
import {SketchContour,} from '../meshProxy/SketchContour';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {SketchLineMaterial} from '../../material';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {SketchCloneContext} from './SketchCloneContext';
import {disposeSketchContour} from './clearSketchContourDisplays';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';
import {refitRaycastAccelerationForGeometry} from '../../raycast-acceleration';

/**
 * {@link SketchCircleObject} 构造参数。
 * 通过圆心世界坐标和半径即可构造完整圆对象，内部自动创建两个控制点。
 */
export interface SketchCircleObjectOptions {
    /** 圆心世界坐标。 */
    center: THREE.Vector3;
    /** 圆半径（参考平面局部距离）。 */
    radius: number;
    /** 外部提供的圆心控制点。 */
    centerPoint?: SketchPointObject;
    /** 外部提供的半径端点控制点。 */
    edgePoint?: SketchPointObject;

    /** 圆心控制点的捕捉来源点；仅记录引用关系，不表示拓扑共点。 */
    centerSourcePoint?: SketchPointObject | null;
    /** 半径端点控制点的捕捉来源点；仅记录引用关系，不表示拓扑共点。 */
    edgeSourcePoint?: SketchPointObject | null;

    /** 可选渲染材质。 */
    material?: SketchLineMaterial;
    /** 创建时所属抽象参考面。 */
    referencePlane: ReferencePlane;
    /** 是否为构造线（默认 false）。 */
    isConstruction?: boolean;
}

/**
 * 草图圆网格对象 * 职责 * 1. 使用 THREE.Line（圆弧采样点序列）表达一个草图圆 * 2. 公共几何逻辑（长度、角度、约束求解）由抽象基类提供 */
export class SketchCircleObject extends AbstractSketchEdgeObject {
    /** 对象运行时类型标记*/
    public readonly objectType = SketchEntityKind.Circle;
    /** 圆对象属于封闭几何。 */
    public get isClosedGeometry(): boolean {
        return true;
    }
    /** 创建时所属抽象参考面。 */
    public referencePlane: ReferencePlane;

    /**
     * 获取圆的定义层圆心世界坐标（实时派生，只读）。
     *
     * 设计说明：
     * 1. 圆的圆心唯一事实来源是圆心控制点（circleStartPoint），圆环几何、
     *    约束求解、捕捉均以其 position 为准；
     * 2. 早期实现曾以 center+radius 字段存储圆定义，vec3dCenter 作为定义层
     *    圆心快照，但后续重构为控制点驱动后该快照未纳入任何位移同步链路，
     *    导致偏置（offset）等功能在圆被移动后读到过期圆心；
     * 3. 为避免再次出现"第三份圆心存储"导致的断链，这里不再保存任何字段，
     *    直接由圆心控制点实时派生，保证所有读取方永远拿到最新圆心；
     * 4. 仅提供 getter，任何写入都会在编译期报错。
     */
    public get vec3dCenter(): THREE.Vector3 {
        return this.getCenterWorld();
    }

    isSketchCircleObject = true
    /** 圆对象的起始控制点（内部使用，不对外暴露startPoint 字段）*/
    private circleStartPoint: SketchPointObject;

    /** 圆对象的终止控制点（内部使用，仅作为基类必需的占位端点）*/
    private circleEndPoint: SketchPointObject;

    /** 默认四个象限点。 */
    private readonly quadrantPoints: SketchPointObject[] = [];

    /** 圆半径值（直接存储，不依赖端点距离计算）。 */
    private _radius: number = 0;

    /** 上一次同步后的圆心世界坐标快照。 */
    private lastSyncedCenterWorld: THREE.Vector3 | null = null;

    /** 圆心平移联动期间的重入保护标记。 */
    private isCenterTranslateSyncing = false;

    /** 圆心控制点是否由圆对象内部创建并持有父级。 */
    private readonly ownsCenterControlPoint: boolean;

    /** 是否属于封闭圆几何*/
    public isClosedGeometryCircle = false;

    /** 实例计数，用于生成显示名称*/
    static count = 0;

    public get baseDisplayName(): string {
        return '圆';
    }

    /**
     * 获取圆心在参考平面局部坐标系中的 2D 坐标。
     * @returns 圆心局部 2D 坐标。
     */
    get centerPoint(): THREE.Vector2 {
        return this.referencePlane.worldToLocal(this.circleStartPoint.getPosition());
    }

    /**
     * 设置圆心到指定的参考平面局部 2D 坐标，并重建圆几何。
     * 内部通过计算位移增量同步移动圆心和半径端点，保持半径不变。
     * @param value 新的圆心局部 2D 坐标。
     */
    set centerPoint(value: THREE.Vector2) {
        const newCenterWorld = this.referencePlane.localToWorld(value);
        const delta = newCenterWorld.clone().sub(this.circleStartPoint.getPosition());
        if (delta.lengthSq() <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared) {
            return;
        }
        // 仅移动圆心控制点，半径由 _radius 字段决定
        this.circleStartPoint.setPosition(newCenterWorld);
        this.syncEdgePointFromRadius();
        this.updateGeometry();
    }

    /**
     * 获取当前圆的半径值。
     * @returns 圆半径（直接返回存储值）。
     */
    get radius(): number {
        return this._radius;
    }

    /**
     * 设置圆的半径值，并重建圆几何。
     * @param value 新的半径值，必须为正有限数。
     */
    set radius(value: number) {
        if (!Number.isFinite(value) || value <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }
        this._radius = value;
        this.syncEdgePointFromRadius();
        this.updateGeometry();
    }

    /**
     * 构造草图圆对象。
     * 通过圆心世界坐标和半径构造，内部自动创建圆心控制点和半径端点。
     * @param options 构造参数（圆心 + 半径）。
     */
    public constructor(options: SketchCircleObjectOptions) {
        // 根据圆心和半径创建两个内部控制点
        const controlPointMaterial = PointsMaterialFactory.getSketchControlPointSpriteMaterial();
        const centerControlPoint = options.centerPoint
            ?? new SketchPointObject({
                position: options.center.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: controlPointMaterial,
                referencePlane: options.referencePlane,

                sourcePoint: options.centerSourcePoint ?? null,

            });
        // 半径端点位于圆心右侧（沿参考平面局部 x 轴正方向偏移 radius）
        const centerLocal = options.referencePlane.worldToLocal(options.center);
        const edgeLocal = new THREE.Vector2(centerLocal.x + options.radius, centerLocal.y);
        const edgeWorld = options.referencePlane.localToWorld(edgeLocal);
        const edgeControlPoint = options.edgePoint
            ?? new SketchPointObject({
                position: edgeWorld,
                pointKind: SketchPointKind.ControlPoint,
                material: controlPointMaterial,
                referencePlane: options.referencePlane,

                sourcePoint: options.edgeSourcePoint ?? null,

            });
        super({
            material: options.material,
            isConstruction: options.isConstruction,
        });
        this.referencePlane = options.referencePlane;
        this.circleStartPoint = centerControlPoint;
        this.circleEndPoint = edgeControlPoint;
        this.ownsCenterControlPoint = !options.centerPoint;
        this.circleStartPoint.registerConnectedEdge(this);
        this.circleEndPoint.registerConnectedEdge(this);
        this.registerRelatedSketchPoints([this.circleStartPoint, this.circleEndPoint]);
        this.circleEndPoint.attachEndpointRole();
        this._radius = options.radius;
        /**
         * 圆对象将“起点控制点”作为圆心控制点使用。
         * 为保证圆被加入场景后圆心控制点可见，这里将其挂载为圆对象子节点。
         */
        this.attachCenterControlPoint(this.circleStartPoint);

        /**
         * 记录初始圆心快照，用于后续“拖拽圆心保持半径”增量计算。
         */
        this.lastSyncedCenterWorld = this.circleStartPoint.getPosition();

        this.name = `${this.baseDisplayName}${SketchCircleObject.count}`;
        SketchCircleObject.count += 1;

        // 构造时创建四个象限点，与圆弧象限点保持一致的生命周期管理。
        this.createDefaultQuadrantPoints(controlPointMaterial);

        /**
         * 构造完成后立即初始化圆几何。
         * 说明：
         * 1. 多边形辅助圆在创建后会立刻参与交互态材质刷新；
         * 2. 若此时圆几何尚未生成，虚线材质调用 computeLineDistances 会访问空顶点导致异常；
         * 3. 在构造阶段完成几何初始化可确保内切/外接辅助圆稳定显示。
         */
        this.updateGeometry();
    }

    /**
     * 获取圆心控制点对象。
     * @returns 圆心控制点。
     */
    public getCenterControlPoint(): SketchPointObject {
        return this.circleStartPoint;
    }

    /**
     * 获取半径端点控制点对象。
     * @returns 半径端点控制点。
     */
    public getEdgeControlPoint(): SketchPointObject {
        return this.circleEndPoint;
    }


    /**
     * 判断当前圆对象是否允许拖动。
     * 说明：
     * 1. 先检查圆对象自身固定约束；
     * 2. 再检查圆心控制点与半径端点是否固定；
     * 3. 任一控制点固定时，整圆视为不可拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public override canDrag(): boolean {
        return super.canDrag()
            && !this.circleStartPoint.isFixed()
            && !this.circleEndPoint.isFixed();
    }


    /**
     * 获取默认四个象限点。
     * @returns 象限点列表。
     */
    public getQuadrantPoints(): readonly SketchPointObject[] {
        return [...this.quadrantPoints];
    }


    /**
     * 复制当前圆对象，返回与源对象完全解耦的新实例。
     * @param context 复制上下文。
     * @returns 当前圆对象的独立副本。
     * @throws 当源圆与副本的象限点数量不一致时抛出错误。
     */
    public cloneSelf(context?: SketchCloneContext): SketchCircleObject {
        const clonedCircle = new SketchCircleObject({
            center: this.getCenterWorld(),
            radius: this.radius,
            material: this.createCloneBaseOptions().material,
            referencePlane: this.referencePlane,
            isConstruction: this.isConstruction,
        });
        this.registerObjectClone(context, clonedCircle);

        clonedCircle.baseStyle = this.baseStyle;
        clonedCircle.getCenterControlPoint().visible = this.circleStartPoint.visible;
        clonedCircle.getEdgeControlPoint().visible = this.circleEndPoint.visible;
        clonedCircle.getCenterControlPoint().isActiveForSnap = this.circleStartPoint.isActiveForSnap;
        clonedCircle.getEdgeControlPoint().isActiveForSnap = this.circleEndPoint.isActiveForSnap;

        if (context) {
            context.pointMap.set(this.circleStartPoint.uuid, clonedCircle.getCenterControlPoint());
            context.pointMap.set(this.circleEndPoint.uuid, clonedCircle.getEdgeControlPoint());
            context.objectMap.set(this.circleStartPoint.uuid, clonedCircle.getCenterControlPoint());
            context.objectMap.set(this.circleEndPoint.uuid, clonedCircle.getEdgeControlPoint());

            const sourceQuadrantPoints = this.getQuadrantPoints();
            const clonedQuadrantPoints = clonedCircle.getQuadrantPoints();
            if (sourceQuadrantPoints.length !== clonedQuadrantPoints.length) {
                throw new Error('圆对象象限点数量非法，无法复制。');
            }
            sourceQuadrantPoints.forEach((point, index) => {
                clonedQuadrantPoints[index].visible = point.visible;
                clonedQuadrantPoints[index].isActiveForSnap = point.isActiveForSnap;
                context.pointMap.set(point.uuid, clonedQuadrantPoints[index]);
                context.objectMap.set(point.uuid, clonedQuadrantPoints[index]);
            });
        } else {
            const sourceQuadrantPoints = this.getQuadrantPoints();
            const clonedQuadrantPoints = clonedCircle.getQuadrantPoints();
            if (sourceQuadrantPoints.length !== clonedQuadrantPoints.length) {
                throw new Error('圆对象象限点数量非法，无法复制。');
            }
            sourceQuadrantPoints.forEach((point, index) => {
                clonedQuadrantPoints[index].visible = point.visible;
                clonedQuadrantPoints[index].isActiveForSnap = point.isActiveForSnap;
            });
        }

        if (this.contour) {
            clonedCircle.syncContour();
        }

        // 复制圆对象的单目标几何关系（如固定约束）。
        this.cloneSingleTargetConstraintsTo(clonedCircle, context);
        this.syncCommonDisplayStateToClone(clonedCircle);
        return clonedCircle;
    }


    // ─── 抽象方法实现 ──────────────────────────────────────────────────────────

    protected getEdgeStartPoint(): SketchPointObject {
        return this.circleStartPoint;
    }

    protected getEdgeEndPoint(): SketchPointObject {
        return this.circleEndPoint;
    }

    protected setEdgeStartPoint(point: SketchPointObject): void {
        /**
         * 替换圆心控制点时同步维护可视化挂载关系，避免旧圆心控制点残留显示。
         */
        this.detachCenterControlPoint(this.circleStartPoint);
        this.unregisterRelatedSketchPoint(this.circleStartPoint);
        this.circleStartPoint = point;
        this.registerRelatedSketchPoint(this.circleStartPoint);
        this.attachCenterControlPoint(this.circleStartPoint);
        // 圆心替换后同步象限点位置（象限点在构造时已创建，无需重新创建）。
        this.syncQuadrantPoints();
        /**
         * 起点（圆心）替换后同步更新圆心快照，避免首次拖拽产生错误位移增量。
         */
        this.lastSyncedCenterWorld = this.circleStartPoint.getPosition();

    }

    protected setEdgeEndPoint(point: SketchPointObject): void {
        this.unregisterRelatedSketchPoint(this.circleEndPoint);
        this.circleEndPoint.detachEndpointRole();
        this.circleEndPoint = point;
        this.circleEndPoint.attachEndpointRole();
        this.registerRelatedSketchPoint(this.circleEndPoint);
    }


    /**
     * 将圆心控制点挂载为圆对象子节点，并同步圆心角色。
     * @param point 需要挂载的圆心控制点。
     */
    private attachCenterControlPoint(point: SketchPointObject): void {
        // 圆心通过点对象统一角色入口应用十字 SVG，避免构造、替换和恢复链路出现不同材质。
        point.attachCurveCenterControlPointRole();
        // 外部复用点不能被圆对象重挂父级，否则会破坏原实体共享控制点的对象树关系。
        if (this.ownsCenterControlPoint) {
            this.add(point);
        }
    }

    /**
     * 从圆对象上移除圆心控制点，并清理圆心角色。
     * @param point 需要移除的圆心控制点。
     */
    private detachCenterControlPoint(point: SketchPointObject): void {
        if (this.ownsCenterControlPoint && point.parent === this) {
            this.remove(point);
        }
        point.detachCurveCenterControlPointRole();
    }

    //------ 圆静默刷新按控制点重建半径 开始------
    /**
     * 仅刷新几何：先按控制点重建半径，再重画圆，最后把圆周控制点吸附回精确位置。
     *
     * 圆的形状有两处表示——存储的 `_radius` 与「圆心到圆周控制点」的距离。
     * 基类实现只重画几何，于是**外部写者静默改完点位后圆纹丝不动**
     * （参数求解写回、事务回滚都走这条路），控制点与圆环脱节，屏幕上就是两套状态。
     *
     * 半径关系锁定时不做反推：锁定值是主尺度，只把控制点吸附回锁定圆周
     * （与圆弧的半径锁定刷新同一条纪律）。
     */
    public override refreshGeometryOnly(): void {
        if (!this.hasRadiusConstraint()) {
            const measuredRadius = this.circleStartPoint.getPosition()
                .distanceTo(this.circleEndPoint.getPosition());

            if (Number.isFinite(measuredRadius)
                && measuredRadius > SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
                this._radius = measuredRadius;
            }
        }
        this.updateGeometry();
        this.syncEdgePointSilentlyFromRadius();
        this.syncQuadrantPoints();
    }

    /**
     * 判断当前圆是否存在半径/直径关系。
     *
     * @returns 存在返回 true。
     */
    private hasRadiusConstraint(): boolean {
        return this.constraints.hasType(SketchRelationKind.Radius)
            || this.constraints.hasType(SketchRelationKind.Diameter);
    }

    /**
     * 静默把圆周控制点吸附回半径对应位置（不触发约束求解链）。
     */
    private syncEdgePointSilentlyFromRadius(): void {
        const centerLocal = this.referencePlane.worldToLocal(this.circleStartPoint.getPosition());
        const newEndLocal = new THREE.Vector2(centerLocal.x + this._radius, centerLocal.y);

        this.circleEndPoint.syncPositionSilently(this.referencePlane.localToWorld(newEndLocal));
    }
    //------ 圆静默刷新按控制点重建半径 结束------

    /**     * 根据当前圆心和 _radius 重新定位半径端点。
     * 半径端点始终位于圆心右侧（沿参考平面局部 x 轴正方向偏移 _radius）。
     */
    private syncEdgePointFromRadius(): void {
        const centerLocal = this.referencePlane.worldToLocal(this.circleStartPoint.getPosition());
        const newEndLocal = new THREE.Vector2(centerLocal.x + this._radius, centerLocal.y);
        const newEndWorld = this.referencePlane.localToWorld(newEndLocal);
        this.circleEndPoint.setPosition(newEndWorld);
    }

    /**
     * 创建默认四个象限点。
     * @param material 控制点材质。
     */
    private createDefaultQuadrantPoints(material: THREE.SpriteNodeMaterial): void {
        const positions = this.computeQuadrantPositions();
        positions.forEach((position) => {
            const point = new SketchPointObject({
                position,
                pointKind: SketchPointKind.ControlPoint,
                material,
                referencePlane: this.referencePlane,
            });
            point.visible = false;
            point.isQuadrantPoint = true;
            this.quadrantPoints.push(point);
            this.add(point);
        });
        this.registerRelatedSketchPoints(this.quadrantPoints);
    }

    /**
     * 同步默认四个象限点位置。
     */
    private syncQuadrantPoints(): void {
        const positions = this.computeQuadrantPositions();
        this.quadrantPoints.forEach((point, index) => {
            const position = positions[index];
            if (!position) {
                throw new Error('圆对象象限点数量非法。');
            }
            point.setPosition(position);
        });
    }

    /**
     * 计算默认四个象限点位置。
     * @returns 四个象限点世界坐标。
     */
    private computeQuadrantPositions(): THREE.Vector3[] {
        const centerLocal = this.referencePlane.worldToLocal(this.circleStartPoint.getPosition());
        const localOffsets = [
            new THREE.Vector2(this._radius, 0),
            new THREE.Vector2(0, this._radius),
            new THREE.Vector2(-this._radius, 0),
            new THREE.Vector2(0, -this._radius),
        ];
        return localOffsets.map((offset) => {
            return this.referencePlane.localToWorld(new THREE.Vector2(
                centerLocal.x + offset.x,
                centerLocal.y + offset.y,
            ));
        });
    }

    /**     * 同步端点到圆几何，并在“拖拽圆心控制点”时保持半径不变。
     * 规则：
     * 1. 若触发点为圆心控制点，仅执行整圆平移，不改变半径；
     * 2. 若半径端点被固定，直接抛错阻止非法编辑；
     * 3. 其余场景保持基类默认同步流程。
     * @param movedPoint 触发刷新的控制点。
     */
    public override syncEndpointsToGeometry(movedPoint?: SketchPointObject): void {
        // 圆心移动时同步半径端点位置（半径值不变）
        if (!this.isCenterTranslateSyncing && movedPoint === this.circleStartPoint) {
            this.isCenterTranslateSyncing = true;
            try {
                this.syncEdgePointFromRadius();
            } finally {
                this.isCenterTranslateSyncing = false;
            }
        }

        super.syncEndpointsToGeometry(movedPoint);
        this.lastSyncedCenterWorld = this.circleStartPoint.getPosition();
    }


    /**
     * 原位重写圆采样顶点并同步关联的渲染与拾取缓存。
     *
     * 设计说明：
     * 1. 正式草图圆固定使用 64 段、65 个顶点，更新时必须保留 `BufferGeometry` 与 `position` 属性身份；
     * 2. WebGPU 渲染管线会缓存顶点缓冲绑定，替换并延迟释放几何体可能令当前渲染通道引用失效缓冲；
     * 3. 顶点拓扑保持不变，因此只需重写坐标、刷新包围体并原位校正既有 BVH。
     */

    public updateGeometry(): void {
        const centerWorld = this.circleStartPoint.getPosition();
        const centerLocal = this.referencePlane.worldToLocal(centerWorld);
        const radius = this._radius;

        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }

        const curve = new THREE.EllipseCurve(
            centerLocal.x,
            centerLocal.y,
            radius,
            radius,
            0,
            2 * Math.PI,
            false,
            0,
        );
        const localPoints = curve.getPoints(64);
        const worldPoints = localPoints.map((point) => this.referencePlane.localToWorld(new THREE.Vector2(point.x, point.y)));

        /**
         * `BufferGeometry.setFromPoints()` 在顶点数一致时会重写既有 position 属性并设置 needsUpdate。
         * 圆的采样数固定，因此构造后的所有刷新都能保持 GPU 顶点缓冲对应的资源身份稳定。
         */
        this.geometry.setFromPoints(worldPoints);
        this.geometry.computeBoundingBox();
        this.geometry.computeBoundingSphere();
        refitRaycastAccelerationForGeometry(this.geometry);

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }

        this.syncQuadrantPoints();


        /**
         * 圆存在轮廓时，几何刷新后同步更新轮廓点集，保证轮廓随圆一起移动。
         */
        this.refreshContourGeometry();

    }


    // ─── 圆特有方────────────────────────────────────────────────────────────

    /**
     * 获取当前圆的圆心世界坐标。
     * @returns 圆心世界坐标副本。
     */
    public getCenterWorld(): THREE.Vector3 {
        return this.circleStartPoint.getPosition();
    }

    /**
     * 获取当前圆半径（委托 radius getter）。
     * @returns 圆半径。
     */
    public getRadius(): number {
        return this.radius;
    }


    /**
     * 平移圆（通过平移两个端点实现）。
     * @param delta 位移向量。
     * @throws 当圆心控制点被固定时抛出错误。
     */
    public translateCircle(delta: THREE.Vector3): void {
        if (this.circleStartPoint.isFixed()) {
            throw new Error('Circle center is fixed and translation is blocked.');
        }

        const nextStart = this.circleStartPoint.getPosition().add(delta);
        // 仅移动圆心，半径端点通过 _radius 重新定位
        this.circleStartPoint.setPosition(nextStart);
        this.syncEdgePointFromRadius();
    }

    /**
     * 静默平移圆。
     * 说明：
     * 1. 不触发约束求解；
     * 2. 仅用于参数运行时统一提交几何；
     * 3. 会同步圆心、半径端点和圆几何。
     * @param delta 位移向量。
     * @throws 当圆心控制点被固定时抛出错误。
     */
    public translateCircleSilently(delta: THREE.Vector3): void {
        if (this.circleStartPoint.isFixed()) {
            throw new Error('Circle center is fixed and silent translation is blocked.');
        }

        const nextStart = this.circleStartPoint.getPosition().add(delta);
        this.circleStartPoint.syncPositionSilently(nextStart);

        const centerLocal = this.referencePlane.worldToLocal(this.circleStartPoint.getPosition());
        const newEndLocal = new THREE.Vector2(centerLocal.x + this._radius, centerLocal.y);
        const newEndWorld = this.referencePlane.localToWorld(newEndLocal);
        this.circleEndPoint.syncPositionSilently(newEndWorld);

        this.updateGeometry();
        this.lastSyncedCenterWorld = this.circleStartPoint.getPosition();
    }

    /**
     * 静默设置圆半径。
     * 说明：
     * 1. 不触发约束求解；
     * 2. 仅用于参数运行时统一提交几何；
     * 3. 会同步半径端点和圆几何。
     * @param radius 目标半径。
     */
    public setRadiusSilently(radius: number): void {
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }
        this._radius = radius;
        const centerLocal = this.referencePlane.worldToLocal(this.circleStartPoint.getPosition());
        const newEndLocal = new THREE.Vector2(centerLocal.x + this._radius, centerLocal.y);
        const newEndWorld = this.referencePlane.localToWorld(newEndLocal);
        this.circleEndPoint.syncPositionSilently(newEndWorld);
        this.updateGeometry();
    }

    /**
     * 圆对象始终可创建封闭轮廓。
     * @returns 固定返回 true。
     */
    public canCreateContour(): boolean {
        return !this.isConstruction;
    }

    public syncContour(): void {
        if (!this.canCreateContour()) {
            this.clearContourDisplay();
            return;
        }
        this.clearContourDisplay();
        const contour = this.createContour();
        this.contour = contour;
        this.add(contour);
    }

    /**
     * 清理当前轮廓显示。
     * 说明：
     * 1. 仅移除当前圆对象上挂载的轮廓实例；
     * 2. 同步清空轮廓引用；
     * 3. 不修改圆本体几何。
     */
    public clearContourDisplay(): void {
        const contour = this.contour;
        if (contour && contour.parent === this) {
            this.remove(contour);
        }
        if (contour) {
            disposeSketchContour(contour);
        }
        this.clearContour();
    }
    /**
     * 基于当前圆几何创建轮廓对象。
     * @returns 创建后的轮廓对象。
     */
    public createContour(): SketchContour {
        this.updateGeometry();
        const contour = new SketchContour({
            source: this
        });
        this.contour = contour;
        return contour;
    }


    /**
     * 收集当前圆线几何的世界坐标点序列。
     * @returns 圆几何顶点列表。
     */
    private collectGeometryPoints(): THREE.Vector3[] {
        const position = this.geometry.getAttribute('position');
        if (!position || position.count <= 0) {
            return [];
        }
        const points: THREE.Vector3[] = [];
        for (let index = 0; index < position.count; index += 1) {
            points.push(new THREE.Vector3(
                position.getX(index),
                position.getY(index),
                position.getZ(index),
            ));
        }
        return points;
    }

    /**
     * 刷新已创建轮廓的几何点集。
     */
    private refreshContourGeometry(): void {
        if (!this.contour) {
            return;
        }
        if (!this.canCreateContour()) {
            this.clearContourDisplay();
            return;
        }
        const points = this.collectGeometryPoints();
        if (points.length < 3) {
            return;
        }
        this.contour.updatePoints(points);
    }
}
